import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "../lib/LanguageContext";

const links = [
    { to: "/", key: "home" },
    { to: "/about", key: "about" },
    { to: "/services", key: "services" },
    { to: "/pacific-rentals", key: "rentals" },
    { to: "/maintenance-construction", key: "maintConstruct" },
    { to: "/concierge", key: "concierge" },
    { to: "/blog", key: "blog" },
    { to: "/contact", key: "contact" },
];

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { lang, toggle, t } = useLanguage();
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const isHome = location.pathname === "/";
    const solid = scrolled || !isHome || open;

    return (
        <header
            data-testid="site-navigation"
            className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${solid ? "bg-navy/95 backdrop-blur-md border-b border-gold/20" : "bg-transparent"}`}
        >
            <div className="container-luxe flex items-center justify-between h-20">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center group flex-shrink-0">
                    <img
                        src="/brand/pkm-logo-dark-bg.png"
                        alt="Pacific Key Management"
                        width="860"
                        height="256"
                        className="h-9 sm:h-10 w-auto select-none transition-opacity duration-300 group-hover:opacity-90"
                        style={{ width: "auto" }}
                        draggable="false"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-4 xl:gap-7 lg:ml-10 xl:ml-14">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.key}`}
                            className={({ isActive }) =>
                                `nav-link whitespace-nowrap text-[0.62rem] xl:text-[0.7rem] tracking-[0.22em] xl:tracking-[0.26em] ${isActive ? "text-gold" : "text-pearl hover:text-gold"}`
                            }
                        >
                            {t(`nav.${l.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3 xl:gap-4 lg:ml-6 xl:ml-8">
                    <button
                        onClick={toggle}
                        data-testid="language-toggle"
                        aria-label="Toggle language"
                        className="hidden sm:flex items-center gap-2 text-pearl hover:text-gold transition-colors text-[0.7rem] tracking-[0.28em] uppercase"
                    >
                        <Globe className="w-3.5 h-3.5" strokeWidth={1.25} />
                        {lang === "en" ? "ES" : "EN"}
                    </button>

                    <Link
                        to="/contact"
                        data-testid="nav-cta-consultation"
                        className="hidden 2xl:inline-flex whitespace-nowrap border border-gold/60 text-gold px-5 py-3 text-[0.65rem] tracking-[0.24em] uppercase hover:bg-gold hover:text-navy transition-colors duration-300"
                    >
                        {t("nav.consultation")}
                    </Link>

                    <button
                        className="lg:hidden text-pearl"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="w-6 h-6" strokeWidth={1.25} /> : <Menu className="w-6 h-6" strokeWidth={1.25} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            <div
                className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-navy/98 ${open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}
                data-testid="mobile-menu"
            >
                <div className="container-luxe py-8 flex flex-col gap-5">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`mobile-nav-link-${l.key}`}
                            className={({ isActive }) =>
                                `nav-link py-1.5 ${isActive ? "text-gold" : "text-pearl"}`
                            }
                        >
                            {t(`nav.${l.key}`)}
                        </NavLink>
                    ))}
                    <button
                        onClick={toggle}
                        data-testid="mobile-language-toggle"
                        className="self-start flex items-center gap-2 text-pearl text-[0.7rem] tracking-[0.28em] uppercase pt-4 border-t border-gold/20 w-full"
                    >
                        <Globe className="w-3.5 h-3.5" strokeWidth={1.25} />
                        {lang === "en" ? "Español" : "English"}
                    </button>
                </div>
            </div>
        </header>
    );
}
